<script setup lang="ts">
import { ofetch } from 'ofetch'
import { onMounted, onUnmounted, ref } from 'vue'

interface NewsItem {
  content: string
  date_published: string
  source: string
}

const props = withDefaults(defineProps<{
  apiBase?: string
  size?: number
  refreshInterval?: number
}>(), {
  apiBase: '/api/market',
  size: 30,
  refreshInterval: 300_000,
})

const news = ref<NewsItem[]>([])
const loading = ref(true)
const error = ref(false)

let timer: ReturnType<typeof setInterval> | undefined

function formatTime(dateStr: string) {
  if (!dateStr)
    return ''
  const d = new Date(dateStr)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

async function fetchNews() {
  try {
    loading.value = true
    error.value = false
    const res = await ofetch<{ data: NewsItem[] }>(`${props.apiBase}/news`, {
      query: { size: props.size },
    })
    const data = res.data ?? res as unknown as NewsItem[]
    news.value = Array.isArray(data) ? data : []
  }
  catch (err) {
    console.error('Failed to fetch news:', err)
    error.value = true
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchNews()
  timer = setInterval(fetchNews, props.refreshInterval)
})

onUnmounted(() => {
  if (timer)
    clearInterval(timer)
})
</script>

<template>
  <div class="glass-panel flex flex-col p-3">
    <div class="mb-2 text-xs text-white/60 font-medium">
      最新资讯
    </div>

    <div v-if="error" class="flex flex-1 items-center justify-center text-xs text-white/30">
      新闻加载失败
    </div>

    <div v-else-if="loading && news.length === 0" class="flex flex-1 items-center justify-center text-xs text-white/30">
      加载中...
    </div>

    <div v-else class="min-h-0 flex-1 overflow-y-auto scrollbar-thin space-y-0.5">
      <div
        v-for="(item, index) in news"
        :key="index"
        class="rounded-lg px-2 py-1.5"
      >
        <div class="flex items-start gap-2">
          <span class="mt-0.5 shrink-0 text-[10px] text-white/30 font-mono">{{ formatTime(item.date_published) }}</span>
          <span class="text-xs text-white/75 leading-relaxed">{{ item.content }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.glass-panel {
  background: rgba(12, 12, 14, 0.45);
  backdrop-filter: blur(20px) saturate(1.2);
  -webkit-backdrop-filter: blur(20px) saturate(1.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.scrollbar-thin::-webkit-scrollbar {
  width: 3px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}
</style>
